/* eslint-disable react/no-danger */
/* eslint-disable no-restricted-syntax */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Unit } from '@interfaces/singlePublicBoat';
import { Anchor, Tooltip } from 'antd';
import Link from 'next/link';
import { useIntl } from 'react-intl';
import { Boat, BoatDesigner, BoatDesignerRelationResponseCollection, ComponentBoatsEngine } from 'services/graphql/cms';
import UnitDetail from './UnitDetail';

type BoatOverviewMenuProps = {
  title: string;
  desc: string | string[] | JSX.Element;
  path?: string | string[];
};

function isValidHttpUrl(string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

const getValidUrl = (site?: string) => {
  if (site && site.length > 3) {
    if (isValidHttpUrl(site)) {
      return site;
    }

    return 'https://' + site;
  }

  return null;
};

export const BoatOverviewMenu = (boat: Boat, jumbotronHeight: number): BoatOverviewMenuProps[] => {
  const usedTransId = 'single_boat.overview';

  const { formatMessage } = useIntl();
  const f = (id: string): string => formatMessage({ id: `${usedTransId}.${id}` });

  const { Link } = Anchor;

  return [
    {
      title: f('type'),
      desc: boat?.type?.name,
      path: `/boats/type/${boat?.type?.slug}`,
    },
    {
      title: f('maker'),
      desc: boat?.manufacturer?.name,
      path: `/maker/${boat?.manufacturer?.slug}`,
    },
    {
      title: f('hull_type'),
      desc: boat?.hull_type?.name,
      path: `/boats/hull-type/${boat?.hull_type?.slug}`,
    },
    {
      title: f('hull_material'),
      desc: boat?.hull_material?.name,
    },
    // {
    //   title: f('activity'),
    //   desc: boat?.activities.name,
    // },
    {
      title: f('category'),
      desc: boat?.categories?.map((item) => ' ' + item.name),
      path: boat?.categories?.map((item) => `/boats/category/${item.slug}`),
    },
    {
      title: f('boat_length'),
      desc: <UnitDetail value={boat?.loa as number} unit="ft" />,
    },
    {
      title: null,
      desc: (
        <Anchor affix={false} targetOffset={jumbotronHeight} className="single-public-boat-see-all">
          <Link
            href="#single-public-boat-content"
            title={
              <>
                <p>{f('see_all_detail')}</p> <DownOutlined />
              </>
            }
          />
        </Anchor>
      ),
    },
  ];
};

function isEmptyEngine(item: ComponentBoatsEngine): boolean {
  for (const [key, value] of Object.entries(item)) {
    if (key !== 'power_type' && key !== '__typename' && value) {
      return false;
    }
  }

  return true;
}

type BoatTableProps = {
  title: string;
  link: string;
  menu: {
    title: string;
    link: string;
    subMenu: {
      key: string;
      name: any;
      hasValue: boolean;
      value: any;
      [x: string]: any;
    }[];
  }[];
}[];

export const BoatTable = (boat: Boat): BoatTableProps => {
  const { formatMessage } = useIntl();

  const getLetterCountTrans = (index: number): string => {
    const alphabet = formatMessage({ id: 'alphabet' });
    const letter = alphabet.split('|')[index];

    return letter;
  };

  const f = (key: string, id: string, letterIndex?: number): string => {
    const mainTransId = 'single_boat.table';

    const usedTransIds = {
      title: `${mainTransId}.title.${id}`,
      menu: `${mainTransId}.menu.title.${id}`,
      subMenu: `${mainTransId}.sub_menu.title.${id}`,
    };

    if (letterIndex !== undefined) {
      return formatMessage({ id: usedTransIds[key] }, { letterCount: getLetterCountTrans(letterIndex) });
    }

    return formatMessage({ id: usedTransIds[key] });
  };

  const mapSimpleRow = (
    key: string,
    rowKey: string,
    itemKey: string,
    transId: string,
    unit?: Unit,
  ): { key: string; name: string; hasValue: boolean; value: string | JSX.Element }[] => {
    if (boat[key] && boat[key].length !== 0) {
      return boat[key].map((item: any, index: number) => ({
        key: `${rowKey}-${index}`,
        name: f('subMenu', transId, index),
        hasValue: !!item.value,
        value: unit ? <UnitDetail value={item[itemKey]} unit={unit} /> : item[itemKey],
      }));
    }

    return [
      {
        key: `${rowKey}-default`,
        hasValue: false,
        name: f('subMenu', transId, 0),
        value: null,
      },
    ];
  };

  const mapEngineRow = (): {
    key: string;
    name: string;
    hasValue: boolean;
    empty?: boolean;
    value: string;
  }[] => {
    const items = [];

    const reusedTransId = 'single_boat.table.sub_menu.title.engine';

    if (boat.engine.length > 0) {
      boat.engine.forEach((item: ComponentBoatsEngine, index) => {
        if (isEmptyEngine(item)) {
          return;
        }

        items.push({
          key: `${reusedTransId}-${index}`,
          name: formatMessage({ id: reusedTransId }, { letterCount: getLetterCountTrans(index) }),
          empty: true,
          hasValue: true,
          value: null,
        });

        for (const key in item) {
          if (key !== '__typename' && key !== 'id' && key !== 'power_type') {
            items.push({
              key: `specs-engine-${key}-${index}`,
              hasValue: !!item[key],
              name: formatMessage({ id: `${reusedTransId}.${key}` }),
              value:
                key === 'power'
                  ? item[key]
                    ? `${parseFloat(item[key])?.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${
                        item?.power_type ? item?.power_type?.toLocaleUpperCase() : ''
                      }`
                    : item[key]
                  : item[key],
            });
          }
        }
      });
    }

    return items;
  };

  const mapDesigners = (designers: BoatDesigner[], spec: string) => {
    const items = [];

    if (designers?.length) {
      designers.map((designer, idx) => {
        const { name, website } = designer;

        const url = getValidUrl(website);

        items.push({
          key: `specs-${spec + name}-name-studio-${idx}`,
          hasValue: !!name,
          name: f('subMenu', 'name_studio'),
          value: name,
        });

        items.push({
          key: `specs-${spec + website}-website-${idx}`,
          hasValue: !!url,
          name: f('subMenu', 'website'),
          value: url && (
            <a href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
          ),
        });
      });
    }

    return items;
  };

  const toolTipClassName = 'single-public-boat-tooltip';

  return [
    {
      title: f('title', 'specs'),
      link: 'specs-table',
      menu: [
        {
          title: f('menu', 'main'),
          link: 'specs-main',
          subMenu: [
            {
              key: 'specs-main-maker',
              hasValue: !!boat?.manufacturer?.name,
              name: f('subMenu', 'maker'),
              value: <Link href={`/maker/${boat?.manufacturer?.slug}`}>{boat?.manufacturer?.name}</Link>,
            },
            {
              key: 'specs-main-maker-website',
              hasValue: !!getValidUrl(boat?.manufacturer?.website),
              name: f('subMenu', 'maker_website'),
              value: getValidUrl(boat?.manufacturer?.website) && (
                <a href={getValidUrl(boat?.manufacturer?.website)} target="_blank" rel="noreferrer">
                  {getValidUrl(boat?.manufacturer?.website)}
                </a>
              ),
            },
            {
              key: 'specs-main-model',
              hasValue: !!boat.model,
              name: f('subMenu', 'model'),
              value: boat?.model,
            },
            {
              key: 'specs-main-first-built-hull',
              hasValue: !!boat.year_first,
              name: f('subMenu', 'first_built_hull'),
              value: boat.year_first,
            },
            {
              key: 'specs-main-last-built-hull',
              hasValue: !!boat.year_last,
              name: f('subMenu', 'last_built_hull'),
              value: boat.year_last,
            },
            {
              key: 'specs-main-version',
              hasValue: !!boat.version,
              name: f('subMenu', 'version'),
              value: boat.version,
            },
            {
              key: 'specs-main-price',
              hasValue: boat?.reference_price?.amount ? true : false,
              name: f('subMenu', 'price'),
              value:
                boat?.reference_price?.amount &&
                `${boat?.reference_price?.amount?.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${
                  boat?.reference_price?.currency?.symbol
                }`,
            },
            {
              key: 'specs-main-boat-type',
              hasValue: boat?.type?.name ? true : false,
              name: f('subMenu', 'boat_type'),
              value: <Link href={`/boats/type/${boat?.type?.slug}`}>{boat?.type?.name}</Link>,
            },
            {
              key: 'specs-main-category',
              hasValue: boat?.categories?.length > 0,
              name: f('subMenu', 'category'),
              value: boat?.categories?.map((item) => (
                <Link href={`/boats/category/${item?.slug}`} key={item?.documentId} style={{ marginRight: '8px' }}>
                  {item.name}
                </Link>
              )),
            },
            // {
            //   key: 'specs-main-activity',
            //   hasValue: boat.activities.name ? true : false,
            //   name: f('subMenu', 'activity'),
            //   value: boat.activities.name,
            // },
            {
              key: 'specs-main-hull-type',
              hasValue: !!boat?.hull_type?.name,
              name: f('subMenu', 'hull_type'),
              value: <Link href={`/boats/hull-type/${boat?.hull_type?.slug}`}>{boat?.hull_type?.name}</Link>,
            },
            {
              key: 'specs-main-hull-material',
              hasValue: boat?.hull_material?.name ? true : false,
              name: f('subMenu', 'hull_material'),
              value: <Link href={`/boats/hull-type/${boat?.hull_material?.slug}`}>{boat?.hull_material?.name}</Link>,
            },
            {
              key: 'specs-main-cabins',
              hasValue: !!boat.cabins,
              name: f('subMenu', 'cabins'),
              value: boat.cabins,
            },
            {
              key: 'specs-main-births',
              hasValue: !!boat.berths,
              name: f('subMenu', 'births'),
              value: boat.berths,
            },
            {
              key: 'specs-main-heads',
              hasValue: !!boat.heads,
              name: f('subMenu', 'heads'),
              value: boat.heads,
            },
            // {
            //   key: 'specs-main-notes',
            //   hasValue: boat?.notes ? true : false,
            //   name: 'Notes',
            //   value: boat?.notes ? <p dangerouslySetInnerHTML={{ __html: `${boat?.notes}` }} /> : '-',
            // },
          ],
        },
        {
          title: f('menu', 'general'),
          link: 'specs-general',
          subMenu: [
            {
              key: 'specs-general-top-speed',
              name: f('subMenu', 'top_speed'),
              hasValue: !!boat.top_speed,
              value: <UnitDetail value={boat.top_speed} unit="mph" />,
            },
            {
              key: 'specs-general-cruising-speed',
              name: f('subMenu', 'cruising_speed'),
              hasValue: !!boat.cruising_speed,
              value: <UnitDetail value={boat.cruising_speed} unit="mph" />,
            },
            {
              key: 'specs-general-mast-height',
              hasValue: !!boat.mast,
              name: f('subMenu', 'mast_height'),
              value: <UnitDetail value={boat.mast} unit="ft" />,
            },
            {
              key: 'specs-general-length-overall',
              hasValue: !!boat.loa,
              name: f('subMenu', 'length_overall'),
              value: <UnitDetail value={boat.loa as number} unit="ft" />,
            },
            {
              key: 'specs-general-length-at-waterline',
              hasValue: !!boat.lwl,
              name: f('subMenu', 'length_at_waterline'),
              value: <UnitDetail value={boat.lwl} unit="ft" />,
            },
            {
              key: 'specs-general-beam-overall',
              hasValue: !!boat.boa,
              name: f('subMenu', 'beam_overall'),
              value: <UnitDetail value={boat.boa} unit="ft" />,
            },
            {
              key: 'specs-general-beam-center-line',
              hasValue: !!boat.bcl,
              name: f('subMenu', 'beam_center_ine'),
              value: <UnitDetail value={boat.bcl} unit="ft" />,
            },
            {
              key: 'specs-general-beam-hull',
              hasValue: !!boat.bh,
              name: f('subMenu', 'beam_hull'),
              value: <UnitDetail value={boat.bh} unit="ft" />,
            },
            {
              key: 'specs-general-headroom',
              hasValue: !!boat.headroom,
              name: f('subMenu', 'headroom'),
              value: <UnitDetail value={boat.headroom} unit="ft" />,
            },
            {
              key: 'specs-general-bridgedeck-clearance',
              hasValue: !!boat.bdcl,
              name: f('subMenu', 'bridgedeck_clearance'),
              value: <UnitDetail value={boat.bdcl} unit="ft" />,
            },
            {
              key: 'specs-general-nominal-bridgedeck-clearance',
              hasValue: !!boat.nom,
              name: f('subMenu', 'nominal_bridgedeck_clearance'),
              value: <UnitDetail value={boat.nom} unit="ft" />,
            },
            {
              key: 'specs-general-draft-maximum',
              hasValue: !!boat.draft_max,
              name: f('subMenu', 'draft_maximum'),
              value: <UnitDetail value={boat.draft_max} unit="ft" />,
            },
            {
              key: 'specs-general-draft-minimum',
              hasValue: !!boat.draft_min,
              name: f('subMenu', 'draft_minimum'),
              value: <UnitDetail value={boat.draft_min} unit="ft" />,
            },
            {
              key: 'specs-general-displacement',
              hasValue: !!boat.displacement,
              name: f('subMenu', 'displacement'),
              value: <UnitDetail value={boat.displacement} unit="lb" />,
            },
            {
              key: 'specs-general-tonnage-gross',
              hasValue: !!boat.tonnage_gross,
              name: f('subMenu', 'tonnage_gross'),
              value: <UnitDetail value={boat.tonnage_gross} unit="lt" />,
            },
            {
              key: 'specs-general-tonnage-net',
              hasValue: !!boat.tonnage_net,
              name: f('subMenu', 'tonnage_net'),
              value: <UnitDetail value={boat.tonnage_net} unit="lt" />,
            },
            {
              key: 'specs-general-ballast-weight',
              hasValue: !!boat.ballast_weight,
              name: f('subMenu', 'ballast_weight'),
              value: (
                <UnitDetail value={boat.ballast_weight ? parseInt(`${boat.ballast_weight}`, 10) : null} unit="lb" />
              ),
            },
            {
              key: 'specs-general-ballast-type',
              hasValue: !!boat.ballast_type,
              name: f('subMenu', 'ballast_type'),
              value: boat.ballast_type,
            },
            {
              key: 'specs-general-helm',
              hasValue: !!boat.helm,
              name: f('subMenu', 'helm'),
              value: boat.helm,
            },
            {
              key: 'specs-general-rudder',
              hasValue: !!boat.rudder,
              name: f('subMenu', 'rudder'),
              value: boat.rudder,
            },
            {
              key: 'specs-general-hull-appendages',
              hasValue: false,
              name: f('subMenu', 'hull_appendages.title'),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('hull_appendages', 'specs-general-hull-appendages', 'value', 'hull_appendages'),
            {
              key: 'specs-general-cockpit',
              hasValue: !!boat.cockpit_location,
              name: f('subMenu', 'cockpit'),
              value: boat.cockpit_location,
            },
            {
              key: 'specs-general-unsinkable',
              hasValue: !!boat.unsinkable,
              name: f('subMenu', 'unsinkable'),
              value: boat.unsinkable,
            },
            {
              key: 'specs-general-trailerable',
              hasValue: !!boat.trailerable,
              name: f('subMenu', 'trailerable'),
              value: boat.trailerable,
            },
            {
              key: 'specs-general-ce-classification',
              hasValue: false,
              name: (
                <span>
                  {f('subMenu', 'ce_classification.title')}{' '}
                  <Tooltip
                    overlayClassName={toolTipClassName}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'ce_classification.html'),
                        }}
                      />
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              ),
              empty: true,
              value: null,
            },
            {
              key: 'specs-general-ce-classification-a',
              hasValue: !!boat?.ce_classification?.a,
              name: f('subMenu', 'ce_classification_a'),
              value: boat?.ce_classification?.a || null,
            },
            {
              key: 'specs-general-ce-classification-b',
              hasValue: !!boat?.ce_classification?.b,
              name: f('subMenu', 'ce_classification_b'),
              value: boat?.ce_classification?.b || null,
            },
            {
              key: 'specs-general-ce-classification-c',
              hasValue: !!boat?.ce_classification?.c,
              name: f('subMenu', 'ce_classification_c'),
              value: boat?.ce_classification?.c || null,
            },
            {
              key: 'specs-general-ce-classification-d',
              hasValue: !!boat?.ce_classification?.d,
              name: f('subMenu', 'ce_classification_d'),
              value: boat?.ce_classification?.d || null,
            },
          ],
        },
        {
          title: f('menu', 'designer'),
          link: 'specs-designer',
          subMenu: [
            {
              key: 'specs-designer-navalarchitect ',
              hasValue: boat?.boat_designers?.length ? true : false,
              name: 'Naval architect',
              empty: true,
              value: null,
            },
            ...mapDesigners(boat.boat_designers, 'Naval architect'),
            {
              key: 'specs-designer-exterior-designer',
              hasValue: boat?.boat_exterior_designers?.length ? true : false,
              name: 'Exterior designer',
              empty: true,
              value: null,
            },
            ...mapDesigners(boat.boat_exterior_designers, 'Exterior designer'),
            {
              key: 'specs-designer-interior-designer',
              hasValue: boat?.boat_interior_designers?.length ? true : false,
              name: 'Interior designer',
              empty: true,
              value: null,
            },
            ...mapDesigners(boat.boat_interior_designers, 'Interior designer'),
          ],
        },
        {
          title: f('menu', 'rig_and_sails'),
          link: 'specs-rig-and-sails',
          subMenu: [
            // {
            //   key: 'specs-rig-and-sails-rigging',
            //   hasValue: !!boat.rigging,
            //   name: f('subMenu', 'rigging'),
            //   value: boat.rigging,
            // },
            {
              key: 'specs-rig-and-sails-rotating-spars',
              hasValue: !!boat?.rotating_spars,
              name: f('subMenu', 'rotating_spars'),
              value: boat.rotating_spars,
            },
            {
              key: 'specs-rig-and-sails-mast',
              hasValue: !!boat.mast,
              name: f('subMenu', 'mast'),
              value: boat.mast,
            },
            {
              key: 'specs-rig-and-sails-spars',
              hasValue: !!boat.spars,
              name: f('subMenu', 'spars'),
              value: boat.spars,
            },
            {
              key: 'specs-rig-and-sails-standing-rigging',
              hasValue: !!boat.standing_rigging,
              name: f('subMenu', 'standing_rigging'),
              value: boat.standing_rigging,
            },
            {
              key: 'specs-rig-and-sails-number-of-spreaders',
              hasValue: !!boat.spreaders_level,
              name: f('subMenu', 'number_of_spreaders'),
              value: boat.spreaders_level,
            },
            {
              key: 'specs-rig-and-sails-spreaders-angle',
              hasValue: !!boat.spreaders_angle,
              name: f('subMenu', 'spreaders_angle'),
              value: boat.spreaders_angle,
            },
            {
              key: 'specs-rig-and-sails-upwind-sail-area',
              hasValue: !!boat.upwind_sail_area,
              name: f('subMenu', 'upwind_sail_area'),
              value: <UnitDetail value={boat.upwind_sail_area} unit="sq.ft" />,
            },
            {
              key: 'specs-rig-and-sails-Downwind-sail-area',
              hasValue: !!boat.downwind_sail_area,
              name: f('subMenu', 'downwind_sail_area'),
              value: <UnitDetail value={boat.downwind_sail_area} unit="sq.ft" />,
            },
            {
              key: 'specs-rig-and-sails-mainsail',
              hasValue: false,
              name: f('subMenu', 'mainsail.title'),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('mainsail', 'specs-rig-and-sails-mainsail', 'value', 'mainsail'),
            {
              key: 'specs-rig-and-sails-headsail',
              hasValue: false,
              name: (
                <span>
                  {f('subMenu', 'headsail.title')}{' '}
                  <Tooltip
                    overlayClassName={toolTipClassName}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'headsail.html'),
                        }}
                      />
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              ),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('headsail', 'specs-rig-and-sails-headsail', 'value', 'headsail'),
            {
              key: 'specs-rig-and-sails-spinnaker',
              hasValue: false,
              name: (
                <span>
                  {f('subMenu', 'spinnaker.title')}{' '}
                  <Tooltip
                    overlayClassName={toolTipClassName}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'spinnaker.html'),
                        }}
                      />
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              ),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('spinnaker', 'specs-rig-and-sails-spinnaker', 'value', 'spinnaker'),
          ],
        },
        {
          title: f('menu', 'engine'),
          link: 'specs-engine',
          subMenu: [...mapEngineRow()],
        },
        {
          title: f('menu', 'storage'),
          link: 'specs-storage',
          subMenu: [
            {
              key: 'specs-storage-fuel-tank',
              hasValue: false,
              name: f('subMenu', 'fuel_tank.title'),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('fuel_tank', 'specs-storage-fuel-tank', 'capacity', 'fuel_tank', 'gallon'),
            {
              key: 'specs-storage-water-tank',
              hasValue: false,
              name: f('subMenu', 'water_tank.title'),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('water_tank', 'specs-storage-water-tank', 'capacity', 'water_tank', 'gallon'),
            {
              key: 'specs-storage-holding-tank',
              hasValue: false,
              name: f('subMenu', 'holding_tank.title'),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('holding_tank', 'specs-storage-holding-tank', 'capacity', 'holding_tank', 'gallon'),
            {
              key: 'specs-storage-water-heater',
              hasValue: false,
              name: f('subMenu', 'water_heater.title'),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('water_heater', 'specs-storage-water-heater', 'capacity', 'water_heater', 'gallon'),
          ],
        },
      ],
    },
    {
      title: f('title', 'performance_and_ratios'),
      link: 'performance-and-ratios-table',
      menu: [
        {
          title: f('menu', 'detail'),
          link: 'performance-and-ratios-detail',
          subMenu: [
            {
              key: 'performance-and-ratios-detail-sail-area-to-displacement',
              hasValue: !!boat.sail_area_displacement,
              name: (
                <span>
                  {f('subMenu', 'sail_area_to_displacement.title')}{' '}
                  <Tooltip
                    overlayClassName={toolTipClassName}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'sail_area_to_displacement.html'),
                        }}
                      />
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              ),
              value: boat.sail_area_displacement?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-upwind-sa-to-displacement',
              hasValue: !!boat.upwind_sail_area_displacement,
              name: f('subMenu', 'upwind_sa_to_displacement'),
              value: boat.upwind_sail_area_displacement?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-downwind-sa-to-displacement',
              hasValue: !!boat.downwind_sail_area_displacement,
              name: f('subMenu', 'downwind_sa_to_displacement'),
              value: boat.downwind_sail_area_displacement?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-hull-displacement-speed',
              hasValue: !!boat.displacement_hull_speed,
              name: (
                <span>
                  {f('subMenu', 'hull_displacement_speed.title')}{' '}
                  <Tooltip
                    overlayClassName={toolTipClassName}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'hull_displacement_speed.html'),
                        }}
                      />
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              ),
              value: boat.displacement_hull_speed?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-displacement-to-length',
              hasValue: !!boat.displacement_length,
              name: (
                <span>
                  {f('subMenu', 'displacement_to_length.title')}{' '}
                  <Tooltip
                    overlayClassName={toolTipClassName}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'displacement_to_length.html'),
                        }}
                      />
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              ),
              value: boat.displacement_length?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-ballast-to-displacement',
              hasValue: !!boat.ballast_displacement,
              name: (
                <span>
                  {f('subMenu', 'ballast_to_displacement.title')}{' '}
                  <Tooltip
                    overlayClassName={toolTipClassName}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'ballast_to_displacement.html'),
                        }}
                      />
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              ),
              value: boat.ballast_displacement?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-capsize-screening-formula',
              hasValue: !!boat.csf,
              name: (
                <span>
                  {f('subMenu', 'capsize_screening_formula.title')}{' '}
                  <Tooltip
                    overlayClassName={toolTipClassName}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'capsize_screening_formula.html'),
                        }}
                      />
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              ),
              value: boat.csf?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-stability-speed',
              hasValue: false,
              name: f('subMenu', 'stability_speed'),
              value: null,
            },
            {
              key: 'performance-and-ratios-detail-kelsall-stability-index',
              hasValue: !!boat.ksi,
              name: f('subMenu', 'kelsall_stability_index'),
              value: boat.ksi?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
          ],
        },
      ],
    },
  ];
};
